import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Linkedin, Facebook, Instagram, Youtube } from "lucide-react";

const footerLinks = {
  products: [
    { name: "All Products", path: "/products" },
    { name: "Modular Workstations", path: "/modular-office-workstations-mumbai" },
    { name: "Office Partitions", path: "/office-partitions-and-cubicles" },
    { name: "Corporate Solutions", path: "/corporate-office-furniture-solutions" },
    { name: "Furniture Manufacturer", path: "/office-furniture-manufacturer-mumbai" },
    { name: "Executive Chairs", path: "/products/executive-chairs" },
    { name: "Conference Tables", path: "/products/conference-tables" },
  ],
  solutions: [
    { name: "IT & Technology", path: "/solutions/it-technology" },
    { name: "Corporate HQ", path: "/solutions/corporate-hq" },
    { name: "Co-working Spaces", path: "/solutions/coworking" },
    { name: "BPO / Call Centers", path: "/solutions/bpo" },
    { name: "Startups", path: "/solutions/startups" },
    { name: "Office Partitions", path: "/solutions/office-partitions" },
    { name: "Modular Cabin Partitions Mumbai", path: "/modular-cabin-partitions-mumbai" },
    { name: "Acoustic Phone Booths Mumbai", path: "/acoustic-phone-booths-mumbai" },
    { name: "Acoustic Pods (India)", path: "/acoustic-pods-booths-india" },
    { name: "BPO / Call Centre Furniture", path: "/bpo-call-center-furniture-mumbai" },
    { name: "All Industries", path: "/solutions" },
  ],
  locations: [
    { name: "Mumbai", path: "/office-workspace-solutions-mumbai" },
    { name: "BKC, Mumbai", path: "/office-furniture-bkc-mumbai" },
    { name: "Andheri & Powai", path: "/office-furniture-andheri-powai" },
    { name: "Navi Mumbai", path: "/modular-office-workstations-navi-mumbai" },
    { name: "Thane", path: "/corporate-office-furniture-thane" },
    { name: "Maharashtra", path: "/modular-office-furniture-maharashtra" },
    { name: "Pune", path: "/office-furniture-solutions-pune" },
    { name: "Bangalore", path: "/corporate-office-furniture-bangalore" },
    { name: "Delhi NCR", path: "/office-furniture-solutions-delhi-ncr" },
    { name: "Gurugram", path: "/office-furniture-gurugram" },
    { name: "Hyderabad", path: "/modular-office-furniture-hyderabad" },
    { name: "Chennai", path: "/office-furniture-chennai" },
    { name: "Ahmedabad", path: "/office-furniture-ahmedabad" },
  ],
  company: [
    { name: "About Us", path: "/about" },
    { name: "Case Studies", path: "/case-studies" },
    { name: "Our Process", path: "/process" },
    { name: "Partner With Us", path: "/partners" },
    { name: "Become Distributor", path: "/distributors" },
    { name: "Privacy Policy", path: "/privacy-policy" },
    { name: "Terms & Conditions", path: "/terms" },
    { name: "Refund & Cancellation", path: "/refund-cancellation" },
  ],
  resources: [
    { name: "Blog & Insights", path: "/blog" },
    { name: "Office Layout Guide", path: "/office-layout-seating-plan-guide" },
    { name: "Fast Office Installation", path: "/fast-office-installation" },
    { name: "Sustainable / IGBC Furniture", path: "/sustainable-office-furniture-india" },
    { name: "Buyer's Guide", path: "/buyers-guide" },
    { name: "Testimonials", path: "/testimonials" },
    { name: "Contact Us", path: "/contact" },
    { name: "Client Portal", path: "/portal/login" },
    { name: "Office Furniture Mumbai", path: "/office-furniture-mumbai" },
  ],
};

const socialLinks = [
  { icon: Linkedin, href: "https://www.linkedin.com/in/ezeefit-modular-furniture-50022598/", label: "LinkedIn" },
  { icon: Facebook, href: "https://www.facebook.com/ezeefitmodularfurniturepvtltd", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/ezeefit_modular_furniture", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@ezeefitmodularfurniture9498", label: "YouTube" },
];

export default function Footer() {
  return (
    <footer className="bg-[#0F172A] text-white noise-overlay" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block mb-6" data-testid="footer-logo-link">
              <picture style={{ display: "contents" }}>
                <source srcSet="/media/logo-2026-dark.webp" type="image/webp" />
                <img src="/media/logo-2026-dark.png"
                  alt="Ezeefit Office Systems - Modular Office Furniture Manufacturer in Mumbai Since 2014"
                  width="1059" height="992"
                  className="h-24 w-auto object-contain" loading="lazy" decoding="async"
                  data-testid="footer-logo-image" />
              </picture>
            </Link>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-2">
              <span className="text-white font-semibold">Ezeefit Office Systems</span>
              <span className="text-[#64748B] text-xs ml-2">| Since 2014</span>
            </p>
            <p className="text-[#475569] text-xs mb-4">
              A brand of Ezeefit Modular Furniture Pvt. Ltd.
            </p>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
              Delivering scalable office furniture solutions for growing corporates since 2014. 
              From concept to installation — complete workspace execution under one roof.
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#F97316] shrink-0 mt-0.5" />
                <span className="text-sm text-[#94A3B8]">
                  Mumbai, Maharashtra, India
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#F97316] shrink-0" />
                <a href="tel:+919820596811" className="text-sm phone-flash-dark hover:opacity-90 transition-opacity" data-testid="footer-primary-phone">
                  9820596811
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#94A3B8] shrink-0" />
                <a href="tel:+919820042100" className="text-sm text-[#94A3B8] hover:text-white transition-colors" data-testid="footer-secondary-phone">
                  9820042100
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#F97316] shrink-0" />
                <a href="mailto:hello@ezeefitofficesystems.in" className="text-sm text-[#94A3B8] hover:text-white transition-colors" data-testid="footer-primary-email">
                  hello@ezeefitofficesystems.in
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#94A3B8] shrink-0" />
                <a href="mailto:eos@ezeefitofficesystems.in" className="text-sm text-[#94A3B8] hover:text-white transition-colors" data-testid="footer-secondary-email">
                  eos@ezeefitofficesystems.in
                </a>
              </div>
            </div>
          </div>

          {/* Solutions */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">Products</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-[#94A3B8] hover:text-[#F97316] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">Solutions</h4>
            <ul className="space-y-3">
              {footerLinks.solutions.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-[#94A3B8] hover:text-[#F97316] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Locations */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">Locations</h4>
            <ul className="space-y-3">
              {footerLinks.locations.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-[#94A3B8] hover:text-[#F97316] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-[#94A3B8] hover:text-[#F97316] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-wider mb-6">Resources</h4>
            <ul className="space-y-3">
              {footerLinks.resources.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-[#94A3B8] hover:text-[#F97316] transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-16 pt-8 border-t border-[#1E293B] flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#94A3B8]">
            <span className="px-3 py-1.5 border border-[#334155] rounded-sm tracking-wide">ISO 9001:2015 Certified</span>
            <span className="px-3 py-1.5 border border-[#334155] rounded-sm tracking-wide">IGBC Certificate of Compliance</span>
          </div>
          
          <p className="text-sm text-[#64748B] text-center">
            © {new Date().getFullYear()} Ezeefit Modular Furniture Pvt. Ltd.
          </p>

          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a
                key={social.label}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center border border-[#334155] hover:border-[#F97316] hover:bg-[#F97316] transition-all duration-300"
                aria-label={social.label}
              >
                <social.icon className="w-5 h-5" />
              </a>
            ))}
          </div>
        </div>

        {/* Legal Links Bar - Always visible for Google Ads compliance */}
        <div className="mt-6 pt-6 border-t border-[#1E293B] flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#94A3B8]">
          <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-[#F97316] transition-colors">Privacy Policy</Link>
          <span className="text-[#334155]">·</span>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#F97316] transition-colors">Terms &amp; Conditions</Link>
          <span className="text-[#334155]">·</span>
          <Link to="/refund-cancellation" data-testid="footer-refund-link" className="hover:text-[#F97316] transition-colors">Refund &amp; Cancellation</Link>
          <span className="text-[#334155]">·</span>
          <Link to="/contact" className="hover:text-[#F97316] transition-colors">Contact Us</Link>
          <span className="text-[#334155]">·</span>
          <Link to="/about" className="hover:text-[#F97316] transition-colors">About</Link>
        </div>
      </div>

      {/* SEO Paragraph */}
      <div className="border-t border-white/10 mt-10 pt-8 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <p className="text-xs text-[#64748B] leading-relaxed max-w-4xl">
          Ezeefit is a modular office furniture manufacturer in Mumbai offering complete office furniture solutions including workstations, chairs, partitions, and turnkey corporate office setups across Mumbai, Thane, and Navi Mumbai. As a trusted office furniture supplier in Mumbai, we provide scalable office furniture systems, workspace design and execution, and end-to-end office setup services for corporates, startups, and public sector offices across Maharashtra and India. Whether you want to buy office furniture in Mumbai, need office furniture setup services in Mumbai, or are looking for office expansion solutions — Ezeefit delivers. We also serve as a modular office furniture manufacturer covering Pune, Mumbai, and pan-India. Buy office furniture in Navi Mumbai or Thane with the same quality and reliability. Office furniture setup services Navi Mumbai and office furniture setup services Thane — delivered on time. Corporate furniture Navi Mumbai India and corporate furniture Thane — trusted since 2014.
        </p>
        <p className="text-xs text-[#94A3B8]/50 mt-3">GSTIN: 27AADCE6117G1ZT | Ezeefit Modular Furniture Private Limited (Ezeefit Office Systems), 6th Floor, A/611-612, Crystal Plaza Premises, New Link Road, Andheri West, Mumbai 400053, Maharashtra, India</p>
      </div>

      {/* WhatsApp Float */}
      <a
        href="https://api.whatsapp.com/send?phone=919820596811&text=Hi,%20I%20want%20to%20enquire%20about%20corporate%20furniture%20solutions"
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-float"
        data-testid="whatsapp-btn"
        aria-label="Chat on WhatsApp"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </footer>
  );
}
